// ── Reading a QR out of an uploaded image ───────────────────────────────────────
// Why this exists: a payee who uploads their UPI QR but never types their UPI id leaves us with a
// picture and nothing else — no VPA means no upi:// link, so the only thing a tap could offer was
// "save the image", and payers ended up paying offline. A UPI QR *contains* the VPA
// (upi://pay?pa=…), so decoding the picture recovers it and every deep-link path works again.
//
// Two decoders, in cost order:
//   1. BarcodeDetector — built into Chrome/Android. Free: no bytes shipped, no canvas work.
//   2. jsQR — dynamically imported so CRA splits it into its own chunk. Browsers with the native
//      API never download it; Safari/iOS, which needs it most, does.
//
// Nothing here throws. A code that won't decode has to degrade to the old behaviour, never break
// the page it's on — this runs both in an admin's settings form and in a payer's payment panel.

// Decoding a full-screen phone screenshot at native resolution is slow and no more accurate than
// decoding a sensible downscale, so cap the long edge.
const MAX_DECODE_PX = 1000;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Harmless for the data URLs we store; matters if a QR is ever an https URL.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('not an image'));
    img.src = src;
  });
}

function toImageData(img) {
  const ratio = Math.min(MAX_DECODE_PX / img.width, MAX_DECODE_PX / img.height, 1);
  const w = Math.max(1, Math.round(img.width * ratio));
  const h = Math.max(1, Math.round(img.height * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}

// Returns the decoded text, or null if there is no readable QR in the image.
export async function decodeQrFromImageSrc(src) {
  if (!src || typeof src !== 'string') return null;

  let img;
  try {
    img = await loadImage(src);
  } catch {
    return null;
  }

  // 1 — native, where available.
  try {
    if (typeof window !== 'undefined' && 'BarcodeDetector' in window) {
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const found = await detector.detect(img);
      if (found?.length && found[0].rawValue) return found[0].rawValue;
      // Native detector ran and found nothing. Still fall through to jsQR: the two disagree often
      // enough on low-contrast screenshots that trying both is worth one canvas pass.
    }
  } catch {
    // Unsupported format, insecure context, detector throwing — fall through.
  }

  // 2 — jsQR, loaded only if we get here.
  try {
    const imageData = toImageData(img);
    const { default: jsQR } = await import('jsqr');
    // Screenshots of a dark-mode app invert the code; attemptBoth costs one extra pass and rescues
    // exactly that case.
    const result = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'attemptBoth' });
    return result?.data || null;
  } catch {
    return null;
  }
}
